import type { TFunction } from 'i18next';

import i18n from '../../../../../i18n/config';
import type { Leaderboard, PrizePool, TournamentOfChampions } from '../../../../../stores/tournament/types';
import { formatAmountByCoinType } from '../../../../../utils/prize';

import type { ResolvedPrize } from './resolveRoundPrizes';
import { resolveRoundPrizes } from './resolveRoundPrizes';

export type BannerPrizeType = 'GC' | 'SC' | `PLACEHOLDER_${number}`;

export interface BannerV6Prize {
  type: BannerPrizeType;
  amount: string;
}

export interface BannerV6Values {
  totalAmount: number;
  currentLevel: number;
  collectAll: boolean;
  finalCc: string;
  finalSc: string;
  text: { finalRound: string; prizePool: string; tooltipTitle: string; noRewards: string };
  rounds: { prizes: BannerV6Prize[] }[];
  imageAssets: Record<string, string>;
}

const MIN_ROUNDS = 2;
const MAX_ROUNDS = 5;

const LOCALE_BY_LANGUAGE: Record<string, string> = {
  de: 'de-DE',
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
};

export function getNumberLocale(language: string | undefined): string {
  const base = (language ?? 'en').toLowerCase().split(/[-_]/)[0];
  return LOCALE_BY_LANGUAGE[base] ?? 'en-US';
}

function toBannerPrize(prize: ResolvedPrize, imageAssets: Record<string, string>): BannerV6Prize {
  switch (prize.prizeType) {
    case 'CC':
      return { type: 'GC', amount: prize.coinsAmount ?? '0' };
    case 'SC':
      return { type: 'SC', amount: prize.coinsAmount ?? '0' };
    case 'MINI_GAME':
      if (prize.imageUrl) imageAssets[`placeholder_${prize.placeholderNum}`] = prize.imageUrl;
      return { type: `PLACEHOLDER_${prize.placeholderNum ?? 0}`, amount: prize.coinsAmount ?? '0' };
    case 'FREE_SPINS':
    default:
      return { type: `PLACEHOLDER_${prize.placeholderNum ?? 0}`, amount: prize.coinsAmount ?? '0' };
  }
}

/** The final round's prize pool per coin; '0' when that coin has none. */
export function formatFinalPool(
  finalPool: Partial<PrizePool> | null | undefined,
  t: TFunction,
  language: string | undefined = i18n.language,
): { finalCc: string; finalSc: string } {
  const locale = getNumberLocale(language);
  const fmt = (amount: number | undefined, coinType: 'GC' | 'SC') =>
    amount && amount > 0 ? formatAmountByCoinType({ amount, coinType, t, locale }) : '0';
  return { finalCc: fmt(finalPool?.gcCoin, 'GC'), finalSc: fmt(finalPool?.scCoin, 'SC') };
}

export function buildBannerV6Values(
  toc: TournamentOfChampions,
  leaderboard: Leaderboard | undefined,
  t: TFunction,
  language: string | undefined = i18n.language,
): BannerV6Values {
  const locale = getNumberLocale(language);
  const roundCount = toc.rounds.length;
  const totalAmount = Math.min(Math.max(roundCount, MIN_ROUNDS), MAX_ROUNDS);
  const collectAll = toc.currentRoundIndex >= roundCount;
  const currentLevel = collectAll ? totalAmount : Math.min(toc.currentRoundIndex + 1, totalAmount);

  const imageAssets: Record<string, string> = {};
  const rounds = toc.rounds.slice(0, totalAmount).map((round) => ({
    prizes: resolveRoundPrizes(round, leaderboard, t, locale).map((p) => toBannerPrize(p, imageAssets)),
  }));

  const { finalCc, finalSc } = formatFinalPool(toc.rounds[roundCount - 1]?.prizePool, t, language);

  return {
    totalAmount,
    currentLevel,
    collectAll,
    finalCc,
    finalSc,
    text: {
      finalRound: t('tournament.champions.banner.finalRound', 'Finals'),
      prizePool: t('tournament.champions.banner.prizePool', 'PRIZE\nPOOL'),
      tooltipTitle: t('tournament.champions.banner.tooltipTitle', 'PRIZE POOL'),
      noRewards: t('tournament.champions.banner.noRewards', 'No Rewards'),
    },
    rounds,
    imageAssets,
  };
}

/** Once per round reached (and the final win), per event, player and coin mode; `null` until the user is known. */
export function buildBannerEarnKey(
  toc: Pick<TournamentOfChampions, 'championsKey' | 'currentRoundIndex'>,
  values: Pick<BannerV6Values, 'collectAll'>,
  userId: number | string | null | undefined,
  coinType: string,
): string | null {
  if (userId == null) return null;
  return `${userId}:${coinType}:${toc.championsKey}:${toc.currentRoundIndex}:${values.collectAll}`;
}

/** Once per round's tournament, per player and coin mode; `null` until the user is known. */
export function buildFinalTooltipKey(
  toc: Pick<TournamentOfChampions, 'championsKey' | 'currentRoundIndex'>,
  userId: number | string | null | undefined,
  coinType: string,
): string | null {
  if (userId == null) return null;
  return `${userId}:${coinType}:${toc.championsKey}:${toc.currentRoundIndex}`;
}
